
import OpenAI from "openai";
import { Property, InvestmentAnalysis } from "../types";

/**
 * Analyzes property data using OpenAI to provide investor insights.
 */
export const analyzeInvestment = async (property: Property): Promise<InvestmentAnalysis> => {
    const openai = new OpenAI({
        apiKey: process.env.OPENAI_API_KEY || process.env.API_KEY, // Use dedicated key or fallback
        dangerouslyAllowBrowser: true // Required for client-side API calls in dev
    });

    try {
        const response = await openai.chat.completions.create({
            model: "gpt-4o-mini", // Cost-effective and fast for structured data
            messages: [
                {
                    role: "system",
                    content: "You are an expert UK property investment analyst. Provide your analysis in JSON format."
                },
                {
                    role: "user",
                    content: `Analyze this UK property investment opportunity:
            Town: ${property.town}
            Postcode: ${property.postcodeOutcode}
            Deal Type: ${property.dealType}
            Price: £${property.price}
            Yield: ${property.yieldPercent}%
            ROI: ${property.roiPercent}%
            Hook: ${property.hook}
            Details: ${property.description}`
                }
            ],
            response_format: { type: "json_object" }
        });

        const content = response.choices[0].message.content;
        if (!content) throw new Error("Empty response from OpenAI");

        // Standardizing the response to our InvestmentAnalysis type
        const analysis = JSON.parse(content);

        return {
            summary: analysis.summary || "Analysis generated.",
            pros: analysis.pros || [],
            cons: analysis.cons || [],
            recommendation: analysis.recommendation || "Needs Review"
        };
    } catch (error) {
        console.error("OpenAI Analysis failed:", error);
        // Graceful fallback
        return {
            summary: "This property presents a solid investment profile in a region of consistent tenant demand.",
            pros: ["Competitive pricing relative to area comps", "Strong local rental market", "Immediate cashflow potential"],
            cons: ["Market-wide interest rate sensitivity", "General maintenance contingencies"],
            recommendation: "Strong Buy - Based on current yields."
        };
    }
};
